/**
 * Showcase (#produit) — one alternating row per product area, each with a REAL
 * production screenshot and three verified bullet points.
 */

import { useT } from '@/i18n';
import type { ShotId } from '@/routes/landing/landing-media';
import { BrowserFrame, MediaPicture } from './MediaPicture';
import { Icon, PrimaryCta, SectionHeading, useLandingCta } from './shared';

const ROWS = [
  { key: 'autopilot', shot: 'autopilot' },
  { key: 'today', shot: 'today' },
  { key: 'composer', shot: 'composer' },
  { key: 'aiTeam', shot: 'ai-team' },
  { key: 'crm', shot: 'crm' },
  { key: 'email', shot: 'email-templates' },
  { key: 'outreach', shot: 'outreach-consent' },
  { key: 'analytics', shot: 'analytics' },
] as const satisfies readonly { key: string; shot: ShotId }[];

export function Showcase() {
  const { t } = useT();
  const { signup } = useLandingCta();

  return (
    <section id="produit" className="py-16 sm:py-20 lg:py-24">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={t('routes.landing.v2.showcase.eyebrow')}
          title={t('routes.landing.v2.showcase.title')}
          subtitle={t('routes.landing.v2.showcase.subtitle')}
        />

        <div className="mt-14 space-y-16 sm:space-y-24">
          {ROWS.map((row, i) => (
            <article
              key={row.key}
              data-testid={`showcase-${row.key}`}
              className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-12"
            >
              <div className={`lg:col-span-5 ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                <p className="text-sm font-semibold uppercase tracking-widest text-primary">
                  {t(`routes.landing.v2.showcase.${row.key}.eyebrow`)}
                </p>
                <h3 className="mt-3 text-balance text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                  {t(`routes.landing.v2.showcase.${row.key}.title`)}
                </h3>
                <p className="mt-4 text-pretty leading-relaxed text-muted">
                  {t(`routes.landing.v2.showcase.${row.key}.body`)}
                </p>
                <ul className="mt-5 space-y-3">
                  {(['b1', 'b2', 'b3'] as const).map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                        <Icon name="check" className="h-3.5 w-3.5" strokeWidth={2.6} />
                      </span>
                      <span className="text-sm leading-relaxed text-foreground">
                        {t(`routes.landing.v2.showcase.${row.key}.${b}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`lg:col-span-7 ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                <BrowserFrame>
                  <MediaPicture id={row.shot} alt={t(`routes.landing.v2.showcase.${row.key}.alt`)} />
                </BrowserFrame>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-16 flex justify-center">
          <PrimaryCta onClick={() => signup()}>{t('routes.landing.v2.showcase.cta')}</PrimaryCta>
        </div>
      </div>
    </section>
  );
}
